import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/macro";

export const INVALID_EMAIL_WARNING = "INVALID_EMAIL_WARNING";
export const EMAIL_VALIDATED_STORAGE_KEY = "ot-checkout-email-validated";
export const FEEDBACK_STORAGE_KEY = "ot-checkout-feedback";

interface FeedbackFollowUp {
  label: MessageDescriptor;
  question: string;
}

export interface FeedbackOption {
  id: string;
  label: MessageDescriptor | string;
  answer: string;
  followUp?: FeedbackFollowUp;
}

interface FeedbackForm {
  id: string;
  question: string;
  options: FeedbackOption[];
}

export interface StoredFeedbackAnswer {
  optionId: string;
  followUpAnswer?: string;
}

export type StoredFeedback = Record<string, StoredFeedbackAnswer>;

const HOW_DID_YOU_HEAR_OPTIONS: FeedbackOption[] = [
  { id: "google", label: "Google", answer: "Google" },
  { id: "instagram", label: "Instagram", answer: "Instagram" },
  { id: "facebook", label: "Facebook", answer: "Facebook" },
  { id: "tiktok", label: "TikTok", answer: "TikTok" },
  { id: "pinterest", label: "Pinterest", answer: "Pinterest" },
  { id: "youtube", label: "YouTube", answer: "YouTube" },
  {
    id: "friend_family",
    label: msg`Friend / Family`,
    answer: "Friend / Family",
  },
  { id: "influencer", label: msg`Influencer`, answer: "Influencer" },
  { id: "radio", label: "Radio", answer: "Radio" },
  { id: "billboard", label: msg`Billboard`, answer: "Billboard" },
  { id: "app_store", label: "App Store", answer: "App Store" },
  {
    id: "coupon_website",
    label: msg`Coupon Website`,
    answer: "Coupon Website",
  },
];

const DID_YOU_FIND_EVERYTHING_OPTIONS: FeedbackOption[] = [
  {
    id: "found_everything",
    label: msg`Yes, found everything!`,
    answer: "Yes, found everything!",
  },
  {
    id: "missing_brand",
    label: msg`Missing brand`,
    answer: "Missing brand",
    followUp: {
      label: msg`Which brand were you hoping to find?`,
      question: "Which brand were you hoping to find?",
    },
  },
  {
    id: "missing_product_type",
    label: msg`Missing product type`,
    answer: "Missing product type",
    followUp: {
      label: msg`What product were you looking for?`,
      question: "What product were you looking for?",
    },
  },
  {
    id: "missing_size_or_fit",
    label: msg`Missing size or fit`,
    answer: "Missing size or fit",
    followUp: {
      label: msg`Which size or fit did you need?`,
      question: "Which size or fit did you need?",
    },
  },
  {
    id: "missing_colour_or_material",
    label: msg`Missing colour or material`,
    answer: "Missing colour or material",
    followUp: {
      label: msg`Which colour or material were you looking for?`,
      question: "Which colour or material were you looking for?",
    },
  },
  {
    id: "item_out_of_stock",
    label: msg`Item out of stock`,
    answer: "Item out of stock",
  },
  {
    id: "other",
    label: msg`Other`,
    answer: "Other",
    followUp: { label: msg`Tell us more`, question: "Tell us more" },
  },
];

export const FEEDBACK_FORMS = {
  howDidYouHear: {
    id: "how_did_you_first_hear_about_us",
    question: "How did you first hear about us?",
    options: HOW_DID_YOU_HEAR_OPTIONS,
  },
  didYouFindEverything: {
    id: "anything_you_couldnt_find",
    question: "Anything you couldn't find?",
    options: DID_YOU_FIND_EVERYTHING_OPTIONS,
  },
} satisfies Record<string, FeedbackForm>;

export const FEEDBACK_CONFIRMATION_DELAY_MS = 300;
export const FEEDBACK_FOLLOW_UP_DELAY_MS = 300;
export const FEEDBACK_SUBMIT_DELAY_MS = 300;
